import * as icons from '../../../assets/icons'
import { ActivateButton } from '../components/Manager/ActivateButton'
import { Box, CenterBox } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { DeleteModeDrawer } from '../components/Manager/DeleteModeDrawer'
import { Divider } from '@dtx-company/shared-components/src/components/atoms/Divider/index'
import { FC, useMemo } from 'react'
import { FlowpageProps } from '../types/FlowpageProps'
import { FormProps, StyledFormV2 as StyledForm } from '../components/Layout'
import { Image } from '@dtx-company/shared-components/src/components/atoms/Image/index'
import { InlineManagerCard, ManagerProps } from '../components/Manager/Manager'
import { LinkEditSection } from '../../../components/profile/Links/LinkEditSection'
import { LinkInput } from '../components/Inputs'
import { LockedTemplateField } from '../../FlowpageTemplates/LockedTemplateField/LockedTemplateField'
import { ManagerSettings } from '../components/Manager/ManagerSettings'
import {
  PrimaryButton,
  SecondaryButton
} from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { trashCanRed } from '@app/code/src/assets'
import { useFlowpageAnalyticsCollector } from '../../../hooks/useFlowpageAnalyticsCollector'
import { useManagerSettings } from '../components/Manager/useManagerSettings'
import { useNFTFormState } from './hooks'

export type NFTFormProps = Pick<FormProps, 'curr' | 'handleClose' | 'order'>

export const Form: FC<NFTFormProps> = ({ curr, order, handleClose }) => {
  // eslint-disable-next-line
  const memoizedCurr = useMemo(() => curr, [])

  const {
    formTitle,
    formValues,
    handleSubmit,
    errors,
    fetchNFTDetails,
    loadingNFTDetails,
    deleteNFTLink
  } = useNFTFormState({
    curr: memoizedCurr,
    order,
    handleClose
  })
  return (
    <StyledForm onSubmit={handleSubmit}>
      <Box width="100%" display={['none', 'none', 'flex']}>
        <Image src={icons.photoOutlined} height="20px" width="20px" />
        <Spacer mr="8px" />
        <Text color="primary.black" variant="button/general">
          {formTitle}
        </Text>
      </Box>
      <Divider display={['none', 'none', 'block']} />
      <LinkInput
        errors={errors}
        value={formValues.link}
        onChange={async e => {
          await fetchNFTDetails(e.target.value)
        }}
      />
      <Spacer mb="16px" />
      <Box justifyContent="space-between">
        <SecondaryButton
          height="40px"
          width="49%"
          borderColor={'status.errorDark'}
          onClick={deleteNFTLink}
          type="button"
        >
          <Image src={trashCanRed} />
          <Spacer mr="8px" />
          <Text variant="button/general" color="status.errorDark" data-testid="delete-link-btn">
            Delete NFT
          </Text>
        </SecondaryButton>
        <PrimaryButton
          disabled={loadingNFTDetails}
          width="49%"
          height="40px"
          type="submit"
          label={loadingNFTDetails ? 'Loading...' : 'Save NFT'}
        />
      </Box>
    </StyledForm>
  )
}

const NFTComponent: FC<FlowpageProps> = ({ link, preview, editLinkMode }) => {
  const { trackLinkClick } = useFlowpageAnalyticsCollector({
    isPreview: Boolean(preview),
    isEditMode: Boolean(editLinkMode)
  })
  const { actionData } = link

  if (!actionData?.image) {
    return null
  }
  const NFTImage: FC = () => (
    <Box cursor="pointer" justifyContent="center" width="100%" position="relative">
      <Image src={icons.nft} position="absolute" width="60px" top={-4} right={-4} />
      <Image
        cursor="pointer"
        alt={'opensea-nft'}
        src={actionData?.image as string}
        width={'100%'}
        borderRadius={'12px'}
      />
    </Box>
  )
  return editLinkMode ? (
    <NFTImage />
  ) : (
    <a
      onClick={() => trackLinkClick({ link_id: link.id }, { useHeap: true })}
      href={actionData?.link ?? ''}
      target="_blank"
      rel="noreferrer"
      aria-label={`Go to ${actionData.link}`}
    >
      <NFTImage />
    </a>
  )
}

export const Flowpage: FC<FlowpageProps> = ({ link, preview, editLinkMode }: FlowpageProps) => {
  return <NFTComponent link={link} preview={preview} editLinkMode={editLinkMode} />
}

export const Manager: FC<ManagerProps> = ({ link, handle, editWidget }: ManagerProps) => {
  const {
    onDeleteLink,
    deleteLinkModeActive,
    toggleDeleteMode,
    showEditLink,
    setShowEditLink,
    toggleActivate,
    isActive,
    managerContainerStyles,
    hasViewOnlyAccess,
    toggleEditMode,
    childOnClick,
    isTemplateConfigLocked
  } = useManagerSettings(link, editWidget)

  const rightMargin = hasViewOnlyAccess ? '52px' : '12px'
  return (
    <InlineManagerCard
      showEditLink={showEditLink}
      padding={'0'}
      viewOnly={hasViewOnlyAccess || isTemplateConfigLocked}
      {...managerContainerStyles.deactivatedContainerStyles}
    >
      {showEditLink ? (
        <LinkEditSection handleClose={() => setShowEditLink(false)} />
      ) : (
        <CenterBox width="100%" alignItems="center">
          {!isTemplateConfigLocked && (
            <ManagerSettings
              onClickEdit={toggleEditMode}
              onClickDelete={toggleDeleteMode}
              disabled={hasViewOnlyAccess}
              deleteModeActive={deleteLinkModeActive}
              viewOnly={hasViewOnlyAccess}
            />
          )}
          {isTemplateConfigLocked && <LockedTemplateField />}
          <Box alignItems="center" width="100%" onClick={childOnClick}>
            <Image
              cursor="pointer"
              src={link?.actionData?.image as string}
              height="70px"
              borderRadius={'12px'}
              opacity={managerContainerStyles.iconOpacity}
              maxWidth="70px"
            />
            <Spacer mr="12px" />
            <Text
              variant="body/medium"
              fontWeight="600"
              opacity={managerContainerStyles.textOpacity}
              color={managerContainerStyles.titleColor}
            >
              OpenSea NFT
            </Text>
          </Box>
          <>
            <ActivateButton
              viewOnly={hasViewOnlyAccess || isTemplateConfigLocked}
              toggleActivated={toggleActivate}
              activated={isActive}
            />
            <Spacer mr="12px" />
            {!hasViewOnlyAccess && !isTemplateConfigLocked && handle}
            <Spacer mr={rightMargin} />
          </>
        </CenterBox>
      )}
      {!showEditLink && (
        <DeleteModeDrawer
          deleteModeActive={deleteLinkModeActive}
          toggleDeleteMode={toggleDeleteMode}
          onDelete={onDeleteLink}
        />
      )}
    </InlineManagerCard>
  )
}
